import clsx from "clsx";
import { ArrowLeft } from "lucide-react";

import { useBackNavigation } from "@/shared/hooks/useBackNavigation";
import { IconButton } from "./IconButton";

export type BackButtonProps = {
  /** Defaults to "Back to <parent title>". */
  label?: string;
  /** `link` shows the label; `icon` is the compact header form. */
  variant?: "link" | "icon";
  /** Render even when the route has no registered parent (uses history). */
  always?: boolean;
  className?: string;
};

/** Back control whose destination comes from the route registry. */
export function BackButton({
  label,
  variant = "link",
  always = false,
  className,
}: BackButtonProps) {
  const { hasParent, parentTitle, goBack } = useBackNavigation();

  if (!hasParent && !always) return null;

  const text = label ?? (parentTitle ? `Back to ${parentTitle}` : "Back");

  if (variant === "icon") {
    return (
      <IconButton
        aria-label={text}
        icon={<ArrowLeft size={18} />}
        variant="outline"
        onClick={goBack}
        className={className}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={goBack}
      className={clsx(
        "inline-flex items-center gap-1.5 text-sm text-theme-text-soft transition hover:text-theme-text",
        className
      )}
    >
      <ArrowLeft aria-hidden className="h-4 w-4" />
      {text}
    </button>
  );
}
